import { motion } from "framer-motion";
import { waLink, EASE, fbLead } from "./primitives";

const MaskLine = ({ children, delay }) => (
  <span className="block overflow-hidden">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 0.9, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  return (
    <section id="hero" className="relative min-h-screen w-full overflow-hidden flex items-end" data-testid="hero-section">
      {/* Full-bleed media with slow Ken Burns pan */}
      <motion.div
        className="absolute inset-0"
        initial={{ scale: 1.08 }}
        animate={{ scale: 1 }}
        transition={{ duration: 26, ease: "linear" }}
        aria-hidden
      >
        {/* Portrait video for mobile */}
        <video
          className="absolute inset-0 h-full w-full object-cover md:hidden"
          src="/media/hero-portrait.mp4"
          poster="/media/hero-poster.jpg"
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          data-testid="hero-video-mobile"
        />
        {/* Landscape video for desktop/tablet */}
        <video
          className="absolute inset-0 h-full w-full object-cover hidden md:block"
          src="/media/hero-hd.mp4"
          poster="/media/hero-poster.jpg"
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          data-testid="hero-video-desktop"
        />
        <div className="absolute inset-0" style={{ background: "var(--forest)", opacity: 0.005, mixBlendMode: "multiply" }} />
      </motion.div>

      {/* Legibility scrim */}
      <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(23,64,42,0.92) 0%, rgba(23,64,42,0.35) 45%, rgba(23,64,42,0.15) 100%)" }} aria-hidden />
      {/* Top scrim for nav legibility */}
      <div className="absolute inset-x-0 top-0 h-40" style={{ background: "linear-gradient(to bottom, rgba(23,64,42,0.6), transparent)" }} aria-hidden />

      {/* Content bottom-left */}
      <div className="relative z-10 w-full mx-auto max-w-[1500px] px-6 md:px-10 pb-24 md:pb-28">
        <motion.span
          className="eyebrow block mb-6 whitespace-nowrap"
          style={{ color: "var(--champagne)", fontSize: "clamp(0.6rem, 2.7vw, 0.95rem)", letterSpacing: "0.28em", fontWeight: 400 }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.3, ease: EASE }}
        >
          Across India
        </motion.span>

        <h1 className="font-heading font-light leading-[0.98] tracking-[-0.02em]" style={{ color: "var(--ivory)" }} aria-label="Hitakari House. By Invite. For the Circle.">
          <span className="block text-[1.25rem] sm:text-[1.6rem] lg:text-[2rem] tracking-[0.02em] mb-4 opacity-90">
            <MaskLine delay={0.42}>Hitakari House.</MaskLine>
          </span>
          <span className="block text-[3rem] sm:text-[4.5rem] lg:text-[6.2rem]">
            <MaskLine delay={0.58}>By Invite.</MaskLine>
            {" "}
            <MaskLine delay={0.72}>For the Circle.</MaskLine>
          </span>
        </h1>

        <motion.p
          className="font-body italic mt-7 max-w-xl text-2xl md:text-3xl"
          style={{ color: "rgba(245,238,223,0.85)" }}
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.0, ease: EASE }}
        >
          This is not a service. It is hospitality.
        </motion.p>

        <motion.a
          href={waLink()}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => fbLead("whatsapp")}
          className="inline-block mt-10 font-label text-[0.78rem] tracking-[0.24em] uppercase px-9 py-4 font-light"
          style={{ backgroundColor: "var(--ivory)", color: "var(--forest)", transition: "background-color 200ms ease, color 200ms ease" }}
          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "transparent"; e.currentTarget.style.color = "var(--ivory)"; e.currentTarget.style.boxShadow = "inset 0 0 0 1px var(--ivory)"; }}
          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "var(--ivory)"; e.currentTarget.style.color = "var(--forest)"; e.currentTarget.style.boxShadow = "none"; }}
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.25, ease: EASE }}
          data-testid="hero-apply-btn"
        >
          Engage with Us
        </motion.a>
      </div>

      {/* scroll hint */}
      <motion.div
        className="absolute bottom-8 right-8 md:right-10 z-10 hidden md:flex items-center gap-3"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.6 }}
      >
        <span className="eyebrow" style={{ color: "rgba(245,238,223,0.5)" }}>Enter</span>
        <span style={{ width: 1, height: 46, background: "rgba(245,238,223,0.4)" }} />
      </motion.div>
    </section>
  );
}
